import {
  getCountryIso2FromName,
  shortAddressString,
  type LatLng,
  type Location,
  type ParsedAsset,
} from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { ActivityIndicator, View } from 'react-native'
import { SelectLocations } from '~/components/interests/SelectLocations'
import { toLocationKey } from '~/components/locations/utils'
import { Body } from '~/components/typography/Content'
import designTokens from '~/lib/designTokens'
import { trpc } from '~/lib/trpc'

function useAssetLocation(asset: ParsedAsset | null, isEnabled: boolean) {
  let address = asset?.address ?? null

  const needsGeocode = !!asset && (!asset.geom || !address?.neighborhood)
  const geocodeAddressString = address ? shortAddressString(address) : ''

  const { data: geocoded, isLoading } = useQuery(
    trpc.locations.geocodeLocation.queryOptions(
      { location: geocodeAddressString },
      { enabled: needsGeocode && !!geocodeAddressString && isEnabled }
    )
  )

  address = geocoded?.address ?? address

  const assetCoordinates: LatLng | null = asset?.geom ? asset.geom : (geocoded?.coordinates ?? null)

  const assetLocation = useMemo(() => {
    if (!assetCoordinates || !address || isLoading || !isEnabled || !asset) {
      return null
    }
    const label = address.neighborhood || address.city

    return {
      id: toLocationKey({
        id: asset.id,
        label,
        coordinates: assetCoordinates,
      }),
      label,
      coordinates: assetCoordinates,
      postalCode: address.postal_code ?? '',
      countryName: address.country ?? '',
    }
  }, [assetCoordinates, address, isLoading, isEnabled, asset])

  return { assetLocation, assetCoordinates, isLoading }
}

export function InterestLocations({
  asset,
  locations,
  setLocations,
  isForContact = false,
}: {
  asset: ParsedAsset | null
  locations: Location[]
  setLocations: (locations: Location[]) => void
  isForContact?: boolean
}) {
  const { t } = useTranslations('interest')
  const assetLocationEnabled = locations.length === 0
  const countryCode =
    // eslint-disable-next-line i18next/no-literal-string
    getCountryIso2FromName(asset?.address?.country ?? null) ?? 'fi'

  const { assetLocation, assetCoordinates, isLoading } = useAssetLocation(
    asset,
    assetLocationEnabled
  )

  useEffect(() => {
    if (assetLocationEnabled && assetLocation) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
      setLocations([assetLocation])
    }
  }, [assetLocationEnabled, assetLocation, setLocations])

  return (
    <View className="gap-4">
      {isLoading ? (
        <ActivityIndicator size="large" color={designTokens.primary} />
      ) : (
        <>
          <Body className="text-pretty text-center text-sm">
            {t(($) =>
              isForContact
                ? $.locationSelection.instructionsContact
                : $.locationSelection.instructions
            )}
          </Body>
          <SelectLocations
            inputVariant="line-dark"
            initialLocations={locations}
            onChangeLocations={setLocations}
            countryCode={countryCode}
            biasCoordinates={assetCoordinates}
          />
        </>
      )}
    </View>
  )
}
